// =============================================================================
// iter-230ba — PWA Auth Durability Layer (IndexedDB mirror)
// =============================================================================
//
// Why this exists
// ---------------
// iOS Safari, PWA installs, and aggressive browser cleanup can purge
// `sessionStorage` AND `localStorage` unpredictably. When that happens,
// the user is silently logged out — often after the app was installed to
// the home screen, which is the worst possible UX for a PWA.
//
// IndexedDB is the only browser storage with strong durability across
// PWA installs + iOS Safari restarts. We use it as a tertiary "passport"
// that backs up auth tokens. Read order on tab mount:
//
//   1. sessionStorage (per-tab runtime cache)            — fastest
//   2. localStorage   (cross-tab fallback)               — survives tab close
//   3. IndexedDB      (durability fallback)              — survives PWA / iOS purge
//
// The 60+ direct `sessionStorage.getItem('token')` call-sites across the
// codebase keep working unchanged — we only ADD IndexedDB as a backup;
// we don't take sessionStorage away.
//
// Trade-off: same as the existing localStorage mirror — access token is
// mildly more XSS-exposed (now stored in three places instead of one).
// Mitigations remain unchanged: HttpOnly refresh-token cookie + 15 min
// access-token TTL + CSP / React XSS escaping.
// =============================================================================

const DB_NAME = 'aniyor-auth';
const STORE = 'kv';
const DB_VERSION = 1;
const AUTH_KEYS = ['user', 'token', 'refresh_token'];

// In-memory promise to dedupe concurrent open() calls.
let _dbPromise = null;

function isAvailable() {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

function openDB() {
  if (!isAvailable()) return Promise.resolve(null);
  if (_dbPromise) return _dbPromise;

  _dbPromise = new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (_) {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });

  return _dbPromise;
}

async function withStore(mode, fn) {
  const db = await openDB();
  if (!db) return null;
  return new Promise((resolve) => {
    let tx;
    try {
      tx = db.transaction(STORE, mode);
    } catch (_) {
      resolve(null);
      return;
    }
    const store = tx.objectStore(STORE);
    let result = null;
    try {
      result = fn(store);
    } catch (_) {
      resolve(null);
      return;
    }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => resolve(null);
    tx.onabort = () => resolve(null);
  });
}

// ─── Public API ─────────────────────────────────────────────────────────────

export async function idbGet(key) {
  return withStore('readonly', (store) => {
    const r = store.get(key);
    return new Promise((resolve) => {
      r.onsuccess = () => resolve(r.result ?? null);
      r.onerror = () => resolve(null);
    });
  }).then((v) => {
    // Inner promise's value is what we want
    if (v && typeof v.then === 'function') return v;
    return v;
  });
}

export async function idbSet(key, value) {
  return withStore('readwrite', (store) => {
    if (value == null) store.delete(key);
    else store.put(value, key);
    return true;
  });
}

export async function idbDelete(key) {
  return withStore('readwrite', (store) => {
    store.delete(key);
    return true;
  });
}

/**
 * Mirror the three auth keys into IndexedDB. Fire-and-forget — never blocks
 * the calling flow. Called from AuthContext on login() and from persistAuth().
 */
export function mirrorAuthToIDB(payload) {
  if (!isAvailable()) return;
  // payload = { user?, token?, refresh_token? }; null values delete the key.
  AUTH_KEYS.forEach((k) => {
    if (k in payload) {
      // Fire-and-forget
      idbSet(k, payload[k]).catch(() => {});
    }
  });
}

/**
 * Clear the auth mirror. Called from logout(). Fire-and-forget.
 */
export function clearAuthIDB() {
  if (!isAvailable()) return;
  AUTH_KEYS.forEach((k) => idbDelete(k).catch(() => {}));
}

/**
 * Read auth values from IndexedDB. Returns { user, token, refresh_token }
 * with null for any key that doesn't exist. Returns null overall when
 * IndexedDB is unavailable.
 */
export async function readAuthFromIDB() {
  if (!isAvailable()) return null;
  const out = {};
  for (const k of AUTH_KEYS) {
    try {
      out[k] = await idbGet(k);
    } catch (_) {
      out[k] = null;
    }
  }
  return out;
}

/**
 * Tab-mount rehydration: if sessionStorage AND localStorage are both empty,
 * try to populate them from IndexedDB. Returns a diagnostic object the
 * AuthProvider can console.info for visibility into rehydration source.
 *
 * Safe to call multiple times — no-ops if sessionStorage already has a token.
 */
export async function rehydrateFromIDBIfNeeded() {
  if (typeof window === 'undefined') return { rehydrated: false, source: 'no_window' };
  try {
    if (sessionStorage.getItem('token')) {
      return { rehydrated: false, source: 'session_already_set' };
    }
    if (localStorage.getItem('token')) {
      // localStorage mirror will be picked up by the existing
      // rehydrateSessionFromLocal() helper — no need to dip into IDB.
      return { rehydrated: false, source: 'local_available' };
    }
    if (!isAvailable()) return { rehydrated: false, source: 'no_idb' };
    const stored = await readAuthFromIDB();
    if (!stored || !stored.token) {
      return { rehydrated: false, source: 'no_idb_token' };
    }
    AUTH_KEYS.forEach((k) => {
      const v = stored[k];
      if (v != null) {
        try {
          sessionStorage.setItem(k, v);
          localStorage.setItem(k, v);
        } catch (_) { /* quota / private mode */ }
      }
    });
    // eslint-disable-next-line no-console
    console.info('[Auth] Rehydrated session from IndexedDB (PWA durability mirror)');
    return { rehydrated: true, source: 'indexeddb' };
  } catch (_) {
    return { rehydrated: false, source: 'idb_error' };
  }
}
